import type { AgentDefinitionResponse } from '@erp/rpc';
import {
  REMOTE_DESKTOP_PORTAL_URLS,
  type OperatorModelTier,
  OPERATOR_MODEL_TIERS as OPERATOR_MODEL_VALUES,
} from '@erp/shared-types';
import { msg } from 'gt-react';

export type RemoteDesktopExecuteModelTier = OperatorModelTier;

export const OPERATOR_MODEL_VALUES_SET = new Set(OPERATOR_MODEL_VALUES);

/** Computer-use model tiers with display labels (shared by tools tab + steps). */
export const OPERATOR_MODEL_TIERS = [
  {
    value: 'haiku' as const,
    label: msg('Haiku (fastest, cheapest)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'sonnet' as const,
    label: msg('Sonnet (balanced, default)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'opus' as const,
    label: msg('Opus (smartest, most expensive)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gpt' as const,
    label: msg('GPT (OpenAI default, GPT-6.1 Sol)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gpt-luna' as const,
    label: msg('GPT-5.6 Luna (fastest OpenAI)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gpt-terra' as const,
    label: msg('GPT-6.1 Sol (balanced OpenAI)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gpt-sol' as const,
    label: msg('GPT-6.1 Sol (smartest OpenAI)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gpt-astra' as const,
    label: msg('GPT-6 Astra (OpenAI)', {
      context: 'operator model option',
    }),
  },
  {
    value: 'gemini' as const,
    label: msg('Gemini 3.8 Flash', { context: 'operator model option' }),
  },
] as const;

export type RemoteDesktopCredentialsConfig = {
  avdUrl: string;
  email: string;
  password: string;
  otpTargetId: string;
  vmPassword: string;
  desktopName: string;
  authStateName: string;
  headless: boolean;
  desktopLoadWaitSeconds: number;
  credentialId: string;
  vmCredentialId: string;
};

export type EmailModelTier = 'fast' | 'balanced' | 'smart' | 'reasoning';

export type RemoteDesktopSummaryConfig = {
  sendEmail: boolean;
  recipients: string[];
  from: string;
  cc: string[];
  bcc: string[];
  subject: string;
  templateExample: string;
  summaryInstructions: string;
  emailModel: EmailModelTier;
};

export type RemoteDesktopApplicationCredentialsConfig = {
  credentialIds: string[];
};

export type RemoteDesktopExecuteConfig = {
  model: RemoteDesktopExecuteModelTier;
};

export type RemoteDesktopConfig = {
  credentials: RemoteDesktopCredentialsConfig;
  applicationCredentials: RemoteDesktopApplicationCredentialsConfig;
  summary: RemoteDesktopSummaryConfig;
  execute: RemoteDesktopExecuteConfig;
};

export const DEFAULT_REMOTE_DESKTOP_CONFIG: RemoteDesktopConfig = {
  credentials: {
    avdUrl: REMOTE_DESKTOP_PORTAL_URLS.full_desktop,
    email: '',
    password: '',
    otpTargetId: '',
    vmPassword: '',
    desktopName: 'AVD-FullDesktop',
    authStateName: '',
    headless: false,
    desktopLoadWaitSeconds: 10,
    credentialId: '',
    vmCredentialId: '',
  },
  applicationCredentials: {
    credentialIds: [],
  },
  summary: {
    sendEmail: false,
    recipients: [],
    from: '',
    cc: [],
    bcc: [],
    subject: '',
    templateExample: '',
    summaryInstructions: '',
    emailModel: 'fast',
  },
  execute: {
    model: 'sonnet',
  },
};

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function getRemoteDesktopConfig(
  config: AgentDefinitionResponse['config'],
): RemoteDesktopConfig {
  const rawCredentials =
    config && typeof config.credentials === 'object' && config.credentials
      ? (config.credentials as Record<string, unknown>)
      : {};

  const rawSummary =
    config && typeof config.summary === 'object' && config.summary
      ? (config.summary as Record<string, unknown>)
      : {};

  const rawApplicationCredentials =
    config &&
    typeof config.applicationCredentials === 'object' &&
    config.applicationCredentials
      ? (config.applicationCredentials as Record<string, unknown>)
      : {};

  const rawExecute =
    config && typeof config.execute === 'object' && config.execute
      ? (config.execute as Record<string, unknown>)
      : {};

  return {
    credentials: {
      avdUrl: toStringValue(
        rawCredentials.avdUrl,
        DEFAULT_REMOTE_DESKTOP_CONFIG.credentials.avdUrl,
      ),
      email: toStringValue(rawCredentials.email),
      password: toStringValue(rawCredentials.password),
      otpTargetId: toStringValue(rawCredentials.otpTargetId),
      vmPassword: toStringValue(rawCredentials.vmPassword),
      desktopName: toStringValue(
        rawCredentials.desktopName,
        DEFAULT_REMOTE_DESKTOP_CONFIG.credentials.desktopName,
      ),
      authStateName: toStringValue(rawCredentials.authStateName),
      headless: toBoolean(
        rawCredentials.headless,
        DEFAULT_REMOTE_DESKTOP_CONFIG.credentials.headless,
      ),
      desktopLoadWaitSeconds: toNumber(
        rawCredentials.desktopLoadWaitSeconds,
        DEFAULT_REMOTE_DESKTOP_CONFIG.credentials.desktopLoadWaitSeconds,
      ),
      credentialId: toStringValue(rawCredentials.credentialId),
      vmCredentialId: toStringValue(rawCredentials.vmCredentialId),
    },
    applicationCredentials: {
      credentialIds: toStringArray(rawApplicationCredentials.credentialIds),
    },
    summary: {
      sendEmail: toBoolean(
        rawSummary.sendEmail,
        DEFAULT_REMOTE_DESKTOP_CONFIG.summary.sendEmail,
      ),
      recipients: toStringArray(rawSummary.recipients),
      from: toStringValue(rawSummary.from),
      cc: toStringArray(rawSummary.cc),
      bcc: toStringArray(rawSummary.bcc),
      subject: toStringValue(rawSummary.subject),
      templateExample: toStringValue(rawSummary.templateExample),
      summaryInstructions: toStringValue(rawSummary.summaryInstructions),
      emailModel: ['fast', 'balanced', 'smart', 'reasoning'].includes(
        rawSummary.emailModel as string,
      )
        ? (rawSummary.emailModel as EmailModelTier)
        : DEFAULT_REMOTE_DESKTOP_CONFIG.summary.emailModel,
    },
    execute: {
      model: OPERATOR_MODEL_VALUES_SET.has(
        rawExecute.model as OperatorModelTier,
      )
        ? (rawExecute.model as RemoteDesktopExecuteModelTier)
        : DEFAULT_REMOTE_DESKTOP_CONFIG.execute.model,
    },
  };
}
